import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useReportColorMode } from "./reportColorModeContext";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { handleAuthErrorCode } from "../../api/network/errorCodes";
import { useRunCustomReportQuery } from "../../api/network/reportApiServices";
import { TListingQueryParams } from "../../api/store/commonSlice";
import {
  RunCustomReportPayload,
  RunCustomReportPayloadField,
  TReportField,
  TReportFilterRequired,
} from "../../api/types/Report";
import svgIcons from "../../assets/svg/svgIcons";
import { APP_CONFIG, REPORT_TYPES } from "../../constants/constants";
import useConversion from "../../hooks/conversion";
import {
  AGGREGATE_FUNCTION,
  AGGREGATE_OPERATOR,
} from "../../pages/reports/components/popupReportColumnAggregation";
import {
  REPORT_FIELD_OUTPUT_TYPES,
  REPORT_FIELD_OUTPUT_TYPES_NUMBER_ALIGNMENT,
  REPORT_FILTER_TYPES,
  REPORT_SPECIAL_VALUES,
} from "../../pages/reports/constants";
import { useCreateCustomReportContext } from "../../pages/reports/context/createCustomReport.context";
import { convertToReportTableColumn } from "../../pages/reports/util";
import { localStorageKeys } from "../../utils/localStorageItems";
import { TListingColumn } from "../modalEditListingColumns";
import { ReportTableColumn, ReportTableIndexColumn } from "./reportTable";

type TReportTableRowProps = {
  groupNestingLevel: number;
  rowIndex: number;
  rawRowData: any;
  rowData: any;
};

const debugComponent = false;

const ReportTableRow: FC<TReportTableRowProps> = ({
  groupNestingLevel,
  rowIndex,
  rawRowData,
  rowData,
}) => {
  const { t } = useTranslation();
  const colorMode = useReportColorMode();
  const {
    reportType,
    categoryId,
    reportId,
    reportFilters: urlParamReportFilters,
  } = useParams<{
    reportType: string;
    categoryId: string;
    reportId: string;
    reportFilters: string;
  }>();
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { reports: orgReportQueryParams } = listingQueryParams;

  const {
    skipRunReport,
    specificReportResponse,
    runReportParams,
    groupByColumns,
    subGroupByColumns,
    columnsVisibility,
  } = useCreateCustomReportContext();

  // Columns
  const [savedColumns, setSavedColumns] = useState<string[] | undefined>(
    undefined
  );

  const reportVisibleColumns: { [key: string]: string[] } = useSelector(
    (state: any) => state.commonReducer.reportVisibleColumns
  );
  useEffect(() => {
    const reportColumnsKey = localStorageKeys.columns.Reports(
      reportType ?? "",
      categoryId ?? "",
      reportId ?? ""
    );
    setSavedColumns(
      reportVisibleColumns[reportColumnsKey] // || getLocalStorageItem(reportColumnsKey)
    );
  }, [reportId, categoryId, reportType, reportVisibleColumns]);

  const { convertToUnit, DisplayConvertedValue } = useConversion();

  const groupingColumns = [...groupByColumns, ...subGroupByColumns];

  const isGroupingRow =
    groupingColumns.length > 0 && groupNestingLevel < groupingColumns.length;
  const isGroupingRowForConversion =
    groupingColumns.length > 0 &&
    groupNestingLevel < groupingColumns.length - 1;

  const [nextLevelExpanded, setNextLevelExpanded] = useState(false);
  const [nextLevelParams, setNextLevelParams] =
    useState<RunCustomReportPayload>({} as RunCustomReportPayload);

  // fetching report data
  const {
    data: reportData,
    isFetching,
    isError,
    error: reportError,
  } = useRunCustomReportQuery(nextLevelParams, {
    skip:
      skipRunReport || // skipRunReport is set to true when multiple states (columnsSearchKeyword and page) are changing - prevents multiple API calls
      Object.keys(nextLevelParams).length === 0 || // skip if nextLevelParams is empty
      !nextLevelParams.id ||
      !nextLevelExpanded ||
      !nextLevelParams,
  });

  /**
   * Get column data for table and edit column modal
   */
  const allColumns: ReportTableColumn[] = useMemo(() => {
    if (!specificReportResponse?.report_fields) return [];

    // Prepare column data for table
    const columnData: ReportTableColumn[] =
      specificReportResponse?.report_fields
        ?.map(convertToReportTableColumn)
        .filter((_, index) => columnsVisibility[index]?.show) || []; // Filter out columns that are selected

    // Extra column for Sr. No.
    const srNumColumn = {
      header: "#",
      id: "index",
      identifier: "index",
    } as ReportTableIndexColumn;
    return [srNumColumn, ...columnData];
  }, [specificReportResponse?.report_fields, columnsVisibility]);

  // duplicate of `tableData` useMemo @ singleReport.tsx
  const nextLevelRowsData = useMemo(() => {
    return reportData?.results?.map((item: any, index: number) => {
      // Sort data in order of viewable columns and retrieve only viewable column data from item
      let viewableData = allColumns.map((col) => {
        if (col.identifier === "index")
          return (
            orgReportQueryParams.page_size * (orgReportQueryParams.page - 1) +
            index +
            1
          );
        const this_field_config = specificReportResponse?.report_fields?.find(
          (field) =>
            reportType === REPORT_TYPES.STANDARD
              ? field?.id === Number(col?.id)
              : field?.identifier === col?.identifier //field.identifier === col.identifier
        );
        const output_type = this_field_config?.output_type;
        // the following change from `item[col.identifier]` to `item?.[col.id as keyof typeof item]` was needed after
        // the last API change to run report response that was made for dashboard widgets
        const cellData =
          item?.[
            (reportType === REPORT_TYPES.STANDARD
              ? col.id
              : col.identifier) as keyof typeof item
          ]; // item[col.identifier];

        // alignment for number fields
        let alignment = "left";
        const all_number_output_types =
          REPORT_FIELD_OUTPUT_TYPES_NUMBER_ALIGNMENT;
        if (
          Object.values(all_number_output_types).includes(output_type as any)
        ) {
          alignment = APP_CONFIG.REPORTS.NUMBER_CELLDATA_ALIGNMENT;
        }

        // unit conversions
        let convertedCellData = {
          finalData: cellData,
          isConverted: false,
        };
        try {
          // skip conversion if aggregate function is COUNT
          // if (
          //   this_field_config?.aggregate_function !== AGGREGATE_FUNCTION.COUNT
          // ) {
          //   convertedCellData = convertToUnit(
          //     output_type as REPORT_FIELD_OUTPUT_TYPES,
          //     cellData
          //   );
          // }

          // skip conversion only if aggregate function is COUNT and not is grouping row
          if (
            (isGroupingRowForConversion &&
              this_field_config?.aggregate_function !==
                AGGREGATE_FUNCTION.COUNT) ||
            !isGroupingRowForConversion
          ) {
            convertedCellData = convertToUnit(
              output_type as REPORT_FIELD_OUTPUT_TYPES,
              cellData,
              this_field_config?.convert_to_whole_number
            );
          }

          //if it is last level of nesting "skip and convert"
          // if (
          //   isGroupingRow &&
          //   groupNestingLevel < groupingColumns.length - 1 &&
          //   this_field_config?.aggregate_function === AGGREGATE_FUNCTION.COUNT
          // ) {
          //   convertedCellData = {
          //     finalData: cellData,
          //     isConverted: false,
          //   };
          // } else {
          //   convertedCellData = convertToUnit(
          //     output_type as REPORT_FIELD_OUTPUT_TYPES,
          //     cellData
          //   );
          // }
        } catch (error) {
          if (APP_CONFIG.DEBUG.REPORTS) {
            console.error("ERROR IN UNIT CONVERSION", error);
            console.error("cellData", cellData);
            console.error("output_type", output_type);
          }
        }

        const value =
          cellData === REPORT_SPECIAL_VALUES.NULL.FOR_COMPARE // null
            ? REPORT_SPECIAL_VALUES.NULL.FOR_DISPLAY // "N/A"
            : cellData === REPORT_SPECIAL_VALUES.BLANK.FOR_COMPARE // ""
              ? REPORT_SPECIAL_VALUES.BLANK.FOR_DISPLAY // ""
              : convertedCellData.finalData;
        return (
          <>
            {debugComponent && (
              <pre className="absolute text-[8px] mt-4">
                isGr: {isGroupingRow?.toString()} isGrCnv:{" "}
                {isGroupingRowForConversion?.toString()}
              </pre>
            )}
            <DisplayConvertedValue
              alignment={alignment as any}
              convertedCellData={{ ...convertedCellData, finalData: value }}
              originalData={cellData}
              outputType={
                colorMode ? (output_type as REPORT_FIELD_OUTPUT_TYPES) : undefined
              }
            />
          </>
        );
      });
      return viewableData;
    });
  }, [
    reportData?.results,
    allColumns,
    orgReportQueryParams,
    urlParamReportFilters,
    isGroupingRow,
    colorMode,
  ]);

  useEffect(() => {
    if (isError) {
      console.error("reportError", reportError);
      setNextLevelExpanded(false);
      handleAuthErrorCode(reportError as FetchBaseQueryError);
    }
  }, [isError]);

  useEffect(() => {
    if (nextLevelExpanded) {
      const nextGroupNestingLevel = groupNestingLevel + 1;
      const nextIsGroupingRow =
        groupingColumns.length > 0 &&
        nextGroupNestingLevel + 1 <= groupingColumns.length; // + 1 added to make sure that last (non-grouping) level fetches rows without grouping
      // console.log("============ next level");
      // console.log("nextGroupNestingLevel", nextGroupNestingLevel);
      // console.log("nextIsGroupingRow", nextIsGroupingRow);
      const params = {
        ...runReportParams,
        page: 1,
        page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
        report_fields: specificReportResponse?.report_fields.map(
          (field: TReportField, fieldIndex: number) => {
            return {
              field: Number(field.field.all_field_id),
              ...(fieldIndex <= nextGroupNestingLevel && nextIsGroupingRow
                ? {
                    is_group_by: fieldIndex <= nextGroupNestingLevel,
                    group_order: nextIsGroupingRow ? fieldIndex + 1 : undefined,
                  }
                : {}),
              // aggregation params
              ...(fieldIndex > nextGroupNestingLevel &&
              !!field.aggregate_function &&
              nextIsGroupingRow
                ? {
                    aggregate_function: field.aggregate_function,
                    column_type: field.column_type,
                    value: field.value,
                    operator: field.operator,
                  }
                : {}),
            } as RunCustomReportPayloadField;
          }
        ),
        report_filters: [
          ...runReportParams.report_filters,
          ...groupingColumns
            .slice(0, nextGroupNestingLevel)
            .map((column: ReportTableColumn) => {
              const columnValue = rawRowData?.[column.identifier];
              const mappedValue =
                columnValue === REPORT_SPECIAL_VALUES.NULL.FOR_COMPARE || // null
                columnValue === `${REPORT_SPECIAL_VALUES.NULL.FOR_COMPARE}` // "null"
                  ? REPORT_SPECIAL_VALUES.NULL.FOR_API // "NULL"
                  : columnValue === REPORT_SPECIAL_VALUES.BLANK.FOR_COMPARE // ""
                    ? REPORT_SPECIAL_VALUES.BLANK.FOR_API // "BLANK"
                    : columnValue;
              return {
                filter_type: REPORT_FILTER_TYPES.COLUMN,
                operator: "=",
                param: column.identifier,
                value: mappedValue,
              } as TReportFilterRequired;
            }),
        ],
      };
      setNextLevelParams(params);
      // console.log("============ next level params");
      // console.log("report_fields", params.report_fields);
      // console.log("report_filters", params.report_filters);
      // console.log("rawRowData", rawRowData);
      // console.log("allColumns", allColumns);
    }
  }, [nextLevelExpanded]);

  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  useEffect(() => {
    if (savedColumns) {
      setVisibleColumns(savedColumns);
    } else {
      setVisibleColumns(
        columnsVisibility.filter((col) => col.show).map((col) => col.name)
      );
    }
  }, [savedColumns, columnsVisibility]);

  return (
    <React.Fragment key={`row_${rowIndex}`}>
      {/* {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <pre className="absolute text-[8px] leading-[8px]">
          isGroupingRow: {JSON.stringify(isGroupingRow)}
          <br />
          groupNestingLevel: {JSON.stringify(groupNestingLevel)}
        </pre>
      )} */}
      <tr
        data-report-row={groupNestingLevel === 0 ? "" : undefined}
        className={`relative ${isGroupingRow || groupingColumns.length === 0 ? "hover:bg-accent-blue-paleO66" : "bg-gray-100 hover:bg-gray-200"}`} // cursor-pointer removed as it does not make sense to click on a row
      >
        {Object.keys(rowData)
          ?.filter((_, cellIndex) => {
            // no filtration for non-grouping rows
            // no filtration for grouping rows if groupNestingLevel > 0
            if (!isGroupingRow || (isGroupingRow && groupNestingLevel > 0))
              return true;
            // first column is always visible
            if (cellIndex === 0) return true;
            // use columnsVisibility to check if column is visible
            return columnsVisibility[cellIndex - 1]?.show;
          })
          .map((key, i) => {
            // grouping row
            if (isGroupingRow) {
              const groupingColumn = groupingColumns[groupNestingLevel];
              // const this_field_config =
              //   specificReportResponse?.report_fields?.[i - 1];
              const this_field_header = visibleColumns[i - 1];
              const this_field_config =
                specificReportResponse?.report_fields?.find(
                  (field) => field.field.name === this_field_header
                );

              if (i === 0 || Number(key) === groupNestingLevel + 1) {
                // grouping column with expand/collapse button
                return (
                  <td
                    key={`groupNestingLevel${groupNestingLevel}_row_${rowIndex}_cell_${i}`}
                    className={`px-4 py-3 whitespace-nowrap text-xs font-medium leading-6 text-gray-700 border-t border-gray-200`}
                  >
                    <span className="relative flex pe-7">
                      {/* grouping column value */}
                      {typeof rowData[key] === "number" ? (
                        <>&nbsp;</>
                      ) : rawRowData?.[groupingColumn?.identifier] ==
                        REPORT_SPECIAL_VALUES.NULL.FOR_COMPARE ? (
                        REPORT_SPECIAL_VALUES.NULL.FOR_DISPLAY
                      ) : rawRowData?.[groupingColumn?.identifier] ===
                        REPORT_SPECIAL_VALUES.BLANK.FOR_COMPARE ? (
                        REPORT_SPECIAL_VALUES.BLANK.FOR_DISPLAY
                      ) : (
                        rowData[key] // ideally `rowData[key]` should be used as this is converted value
                        // rawRowData?.[groupingColumn?.identifier]
                      )}

                      {/* {rowIndex === 0 && i > 0 && (
                      <pre className="absolute bg-amber-200/80 text-[8px] leading-[8px] z-modal">
                        {JSON.stringify(runReportParams, null, 2)}
                      </pre>
                    )} */}

                      {/* expand/collapse button */}
                      {i > 0 && (
                        <button
                          className="group absolute top-1/2 right-0 -translate-y-1/2 bg-gray-200 disabled:bg-transparent enabled:hover:bg-gray-500 rounded-md size-6"
                          type="button"
                          onClick={() =>
                            setNextLevelExpanded(!nextLevelExpanded)
                          }
                          disabled={isFetching}
                        >
                          {isFetching ? (
                            <img
                              className="size-6 invert"
                              src={svgIcons.LoadingAnimation}
                              alt="loading"
                            />
                          ) : (
                            <img
                              className={`brightness-50 group-enabled:group-hover:brightness-200 transition-all ${nextLevelExpanded ? " rotate-90" : ""}`}
                              src={svgIcons.ChevronRight}
                              alt="chevron-right"
                            />
                          )}
                        </button>
                      )}
                    </span>
                  </td>
                );
              } else {
                // blank cells for grouping row

                // display aggregate function calculation and result - grouping row
                const convertedMultiplicant =
                  !!this_field_config?.aggregate_function &&
                  this_field_config?.operator === AGGREGATE_OPERATOR.MULTIPLY
                    ? parseFloat(
                        this_field_config?.aggregate_function ===
                          AGGREGATE_FUNCTION.COUNT
                          ? `${
                              Number(
                                rawRowData?.[this_field_config?.identifier]
                              ) / Number(this_field_config?.value)
                            }`
                          : (convertToUnit(
                              this_field_config?.output_type as REPORT_FIELD_OUTPUT_TYPES,
                              Number(
                                rawRowData?.[this_field_config?.identifier]
                              ) / Number(this_field_config?.value),
                              this_field_config?.convert_to_whole_number
                            )?.finalData as any as string)
                      )
                    : 1;

                return (
                  <td
                    key={`groupNestingLevel${groupNestingLevel}_row_${rowIndex}_cell_${i}`}
                    className={`px-4 py-3 whitespace-nowrap text-xs font-medium leading-6 text-gray-700 border-t border-gray-200`}
                  >
                    {!!this_field_config?.aggregate_function ? (
                      <>
                        {this_field_config?.operator ===
                        AGGREGATE_OPERATOR.MULTIPLY ? (
                          <span className="flex font-bold">
                            {convertedMultiplicant
                              .toFixed(2)
                              .replace(".00", "")}{" "}
                            * {this_field_config?.value}
                            {` =`}
                            <>&nbsp;</>
                            {rowData[key]}
                          </span>
                        ) : (
                          <span>{rowData[key]}</span>
                        )}
                      </>
                    ) : (
                      <>&nbsp;</>
                    )}
                  </td>
                );
              }
            }
            const column_ids = allColumns.map((item) =>
              reportType === REPORT_TYPES.STANDARD ? item.id : item.identifier
            );
            const keys = ["", ...Object.keys(rawRowData)];
            // regular report row
            // columnsVisibility[Number(key) - 1] is used instead of column_ids/keys lookup
            // because rowData is built from allColumns order (= report_fields order), which matches
            // columnsVisibility order. The previous column_ids.includes(keys?.[Number(key)]) check
            // depended on rawRowData key order matching allColumns order; when the API returns fields
            // in a different order that assumption breaks, causing wrong cells to be hidden and
            // visible data to shift left under incorrect column headers.
            return groupByColumns.length === 0 ? (
              (columnsVisibility[Number(key) - 1]?.show ||
                Number(key) === 0) && (
                <td
                  key={`row_${rowIndex}_cell_${i}`}
                  className={`px-4 py-3 whitespace-nowrap text-xs font-medium leading-6 text-gray-700 border-t border-gray-200`}
                >
                  {rowData[key]}
                </td>
              )
            ) : (
              <td
                key={`row_${rowIndex}_cell_${i}`}
                className={`px-4 py-3 whitespace-nowrap text-xs font-medium leading-6 text-gray-700 border-t border-gray-200`}
              >
                {rowData[key]}
              </td>
            );
          })}
      </tr>

      {/* next set of rows - in case of grouping */}
      {isGroupingRow && nextLevelExpanded && !!reportData && (
        <>
          {!!nextLevelRowsData && nextLevelRowsData.length > 0 ? (
            nextLevelRowsData.map((nextLevelRowData, nextLevelRowIndex) => (
              <ReportTableRow
                groupNestingLevel={groupNestingLevel + 1}
                rowIndex={nextLevelRowIndex}
                rawRowData={reportData.results[nextLevelRowIndex]}
                rowData={nextLevelRowData}
                key={`groupNestingLevel${groupNestingLevel}_row_${nextLevelRowIndex}`}
              />
            ))
          ) : (
            <tr>
              <td
                colSpan={columnsVisibility.length}
                className="text-center text-lg font-bold text-gray-400"
              >
                <div className="sticky left-0 w-report-table-no-items min-h-report-table-no-items flex items-center justify-center pointer-events-none touch-none">
                  {t("no_items_found")}
                </div>
              </td>
            </tr>
          )}
        </>
      )}
    </React.Fragment>
  );
};
export default ReportTableRow;
